import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { AddButton, ButtonsDiv, Container, FormRow, LinkItem, LinksList, RemoveButton, SignUpForm } from './styles';
import Button from 'components/Buttom/Button';
import FieldMask from 'components/FieldMask';
import ErrorMessage from 'components/ErrorMessage';
import { Field, Form, Formik, FormikProps, FormikValues, FieldArray } from 'formik';
import IService from 'interfaces/IService';
import ServiceRestService from 'services/ServiceRestService';
import { useLoading } from 'hooks/useLoading';
import MessageModal from 'common/MessageModal';
import SelectComponent from 'components/Select';

interface IBasicServiceFormProps {
  onCancel: (reload?: boolean) => void;
  editingService: IService | null;
  editingMode: boolean;
}

const parseLink = (l: any) => {
  if (typeof l !== 'string') return l;
  try {
    return JSON.parse(l);
  } catch (e) {
    return { label: '', url: l };
  }
};

const BasicServiceForm = ({ onCancel, editingService, editingMode }: IBasicServiceFormProps): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const form = useRef<FormikProps<FormikValues>>(null);
  const [showModal, setShowModal] = useState(false);
  const [messageInfo, setMessageInfo] = useState({ title: '', message: '' });
  const service = new ServiceRestService();
  const options = [
    { label: `Sim`, value: `Sim` },
    { label: 'Não', value: 'Não' },
  ];

  useEffect(() => {
    openLoading();
    closeLoading();
  }, [openLoading, closeLoading]);

  useEffect(() => {
    if (editingService) {
      form.current?.setValues({
        description: editingService.description || '',
        email: editingService.email || '',
        phone: editingService.phone || '',
        searchTerm: editingService.searchTerm || '',
        options: !!editingService.searchTerm,
        links:
          editingService.links && editingService.links.length > 0
            ? editingService.links.map(parseLink)
            : [{ label: '', url: '' }],
        serviceDescription: editingService.serviceDescription || '',
        openingHours: editingService.openingHours || '',
      });
    } else {
      form.current?.resetForm();
    }
  }, [editingService]);

  const handleChange = (selectedValue: string | string[]) => {
    if (typeof selectedValue === 'string') {
      const optionsValue = selectedValue === 'Sim';
      form.current?.setFieldValue('options', optionsValue);
    }
  };

  const handleCancel = () => {
    onCancel(messageInfo.title === 'Sucesso');
  };

  const validationSchema = yup.object().shape({
    description: yup.string().required('Nome do serviço é obrigatório'),
    email: yup.string().email('Digite um e-mail válido').required('E-mail é obrigatório'),
    phone: yup.string().required('Telefone é obrigatório'),
    searchTerm: yup.string().required('O tipo ou endereço do serviço é obrigatório'),
    options: yup.boolean().required('Selecione uma opção'), // Validação para o select
    serviceDescription: yup.string(),
    openingHours: yup.string(),
  });

  const onSubmit = async (values: any) => {
    openLoading();

    try {
      const linksObj = values.links?.filter((l: any) => l.url && l.url.trim() !== '') || [];
      const links = linksObj.map((l: any) => JSON.stringify(l));

      if (editingMode && editingService) {
        const updateData: IService = {
          ...editingService,
          description: values.description,
          email: values.email,
          phone: values.phone,
          searchTerm: values.searchTerm,
          options: values.options,
          links,
          serviceDescription: values.serviceDescription,
          openingHours: values.openingHours,
        };
        await service.update(editingService.id, updateData);
        setShowModal(true);
        const messageWithSpan = 'O serviço foi <span>editado</span> com sucesso.';
        setMessageInfo({
          title: 'Sucesso',
          message: messageWithSpan,
        });
      } else {
        const createData: Omit<IService, 'id'> = {
          description: values.description,
          email: values.email,
          phone: values.phone,
          searchTerm: values.searchTerm,
          options: values.options,
          links,
          serviceDescription: values.serviceDescription,
          openingHours: values.openingHours,
        };
        await service.create(createData as IService);
        setShowModal(true);
        const messageWithSpan = 'O serviço foi <span>criado</span> com sucesso.';
        setMessageInfo({
          title: 'Sucesso',
          message: messageWithSpan,
        });
      }
    } catch (error) {
      setShowModal(true);
      const messageWithSpan = editingMode
        ? 'Erro ao <span>editar</span> serviço.'
        : 'Erro ao <span>criar</span> serviço.';
      setMessageInfo({
        title: 'Erro',
        message: messageWithSpan,
      });
    }

    closeLoading();
  };

  return (
    <>
      <Formik
        innerRef={form}
        initialValues={{
          description: '',
          email: '',
          phone: '',
          searchTerm: '',
          options: '',
          links: [{ label: '', url: '' }],
          serviceDescription: '',
          openingHours: '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}>
        {({ values }) => (
          <Container>
            <div className="header">
              <h2>Dados do Serviço</h2>
              <span>1/1</span>
            </div>
            <SignUpForm>
              <Form>
                <FormRow>
                  <div className="NameField">
                    <label>Nome do Serviço</label>
                    <Field name="description" />
                    <ErrorMessage name="description" />
                  </div>
                  <div className="NameField">
                    <SelectComponent
                      label="Possui Unidade Física?"
                      options={options}
                      onChange={handleChange}
                      withCheckbox={false}
                      selected={[values.options === true ? 'Sim' : values.options === false ? 'Não' : '']}
                      name="options"
                    />
                    <ErrorMessage name="options" />
                  </div>
                </FormRow>

                <FormRow>
                  <div className="PlaceIDField">
                    <div className="TitleHeader">
                      <label>
                        Tipo de Serviço (ex.: Hospital, Delegacia) ou endereço (ex.: Rua Alfredo Dutra, 1 - Centro,
                        Porto Seguro - BA, 45810-000, Brasil)
                      </label>
                    </div>
                    <Field name="searchTerm" />
                    <ErrorMessage name="searchTerm" />
                  </div>
                </FormRow>
                <FormRow>
                  <div className="NameField">
                    <label>Telefone</label>
                    <FieldMask type="phone">
                      <Field name="phone" />
                    </FieldMask>
                    <ErrorMessage name="phone" />
                  </div>
                  <div className="NameField">
                    <label>E-mail</label>
                    <Field name="email" />
                    <ErrorMessage name="email" />
                  </div>
                </FormRow>

                <FormRow>
                  <div className="PlaceIDField">
                    <div className="TitleHeader">
                      <label className="optional">Descrição do Serviço</label>
                    </div>
                    <Field as="textarea" name="serviceDescription" placeholder="Descreva brevemente o serviço..." />
                    <ErrorMessage name="serviceDescription" />
                  </div>
                </FormRow>

                <FormRow>
                  <div className="PlaceIDField">
                    <div className="TitleHeader">
                      <label className="optional">Dias e Horário de Funcionamento</label>
                    </div>
                    <Field name="openingHours" placeholder="Ex: Segunda a Sexta, das 08:00 às 18:00" />
                    <ErrorMessage name="openingHours" />
                  </div>
                </FormRow>
                <FormRow>
                  <div className="PlaceIDField">
                    <div className="TitleHeader">
                      <label>Links</label>
                    </div>
                    <FieldArray name="links">
                      {({ push, remove }) => (
                        <LinksList>
                          {values.links &&
                            values.links.length > 0 &&
                            values.links.map((link: any, index: number) => (
                              <LinkItem key={index}>
                                <div style={{ display: 'flex', flex: 1, gap: '10px' }}>
                                  <Field name={`links.${index}.label`} placeholder="Nome (ex: Site, Instagram...)" />
                                  <Field name={`links.${index}.url`} placeholder="https://..." />
                                </div>
                                <RemoveButton type="button" onClick={() => remove(index)}>
                                  Remover
                                </RemoveButton>
                              </LinkItem>
                            ))}
                          <AddButton type="button" onClick={() => push({ label: '', url: '' })}>
                            + Adicionar Link
                          </AddButton>
                        </LinksList>
                      )}
                    </FieldArray>
                  </div>
                </FormRow>
                <ButtonsDiv>
                  <Button onClick={handleCancel} text="CANCELAR" />
                  <Button type="submit" text="SALVAR" />
                </ButtonsDiv>
              </Form>
            </SignUpForm>
          </Container>
        )}
      </Formik>

      <MessageModal isOpen={showModal} title={messageInfo.title} message={messageInfo.message} onClose={handleCancel} />
    </>
  );
};

export default BasicServiceForm;
